import { useAuth0 } from "@auth0/auth0-react";
import { Avatar } from "@mui/material";
import Button from "@mui/material/Button";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { ThemeProvider } from "@mui/material/styles";
import { loadAccount } from "actions/account";

import "../wyloo/wyloo-microsite.scss";
import { loadPortal } from "actions/portal";
import { Grid, Form } from "components/lynx-components";
import { LynxDialog } from "components/lynx-dialog";
import useAlert from "hooks/useAlert";
import useWindowDimensions from "hooks/useWindowDimensions";
import { jwtDecode } from "jwt-decode";
import _ from "lodash";
import queryString from "query-string";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { deactivateUser } from "services/auth-service";
import { logUserHistory } from "services/customer-portal-service";
import { clearClientSessionOnLogout } from "services/logout-cleanup";
import { updatePortalUserSelf } from "services/users";
import { validationService } from "services/validation";
import { LocalStorageKeys, PortalUserHistoryActions } from "types/enums";
import LynxLogo from "../../../images/Lynx-logomark-colour-rgb.svg";
import { getAudience } from "./../../../auth-provider-wrapper";
import { wylooTheme } from "./wyloo-theme";

export function WylooPortalNavbar(props) {
  const dispatch = useDispatch();

  const location = useLocation();
  const navigate = useNavigate();
  const { width } = useWindowDimensions();
  const portal = useSelector((state) => state.portal);
  const account = useSelector((state) => state.account);
  const [showProfile, setShowProfile] = useState(false);
  const [ip, setIp] = useState(null);
  const [anchorElAccount, setAnchorElAccount] = React.useState(null);
  const [formState, setFormState] = useState({});
  const [deactivate, setDeactivate] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [showDisabled, setShowDisabled] = useState(false);
  const [showPendingRegistration, setShowPendingRegistration] = useState(false);
  const { showAlert } = useAlert();
  const redirectRoute = `${window.location.origin}/wyloo`;
  const { isAuthenticated, getAccessTokenSilently, logout, loginWithRedirect } =
    useAuth0();

  const performAuth0Logout = async (logoutParams) => {
    await clearClientSessionOnLogout();
    logout(logoutParams);
  };
  useEffect(() => {
    if (isAuthenticated) {
      localStorage.removeItem(LocalStorageKeys.OrganizationUserId);
      dispatch(loadAccount(true));
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!account.isLoading && isAuthenticated) {
      if (account.hasErrored) {
        setShowPendingRegistration(true);
        return;
      }
      if (!account.isActive) {
        setShowDisabled(true);
        return;
      }
      if (!account.hasAcceptedTermsOfUse) {
        const registrationLink = `/register/${account.registrationGuid}`;
        navigate({
          pathname: registrationLink,
        });
        return;
      }
      if (
        !account.registrationApprovedDateTimeUtc &&
        !account.registrationRejectedDateTimeUtc &&
        account.isPortalUser
      ) {
        setShowPendingRegistration(true);
        return;
      }
      if (account.registrationRejectedDateTimeUtc) {
        setShowDisabled(true);
        return;
      }
    }
  }, [account.isLoading, isAuthenticated]);

  useEffect(() => {
    dispatch(loadPortal(location.pathname));
  }, []);
  useEffect(() => {
    if (!account.isLoading) {
      setFormState({
        email: account.email,
        firstName: account.firstName,
        lastName: account.lastName,
      });
    }
  }, [account]);
  const handleInputChange = (e) => {
    const newState = { ...formState };
    const { name, value } = e.target;
    _.set(newState, name, value);

    setFormState(newState);
  };
  useEffect(() => {
    if (!portal.isLoading) {
      document.title = portal.name;
    }
  }, [portal]);

  React.useEffect(() => {
    fetch("https://api.ipify.org?format=json")
      .then((response) => response.json())
      .then((data) => {
        localStorage.setItem(LocalStorageKeys.Ip, data.ip);
        setIp(data.ip);
      })
      .catch((error) => {
        localStorage.setItem(LocalStorageKeys.Ip, "");
        setIp("");
      });
  }, []);
  React.useEffect(() => {
    if (ip != null) {
      logUserHistory({
        url: location.pathname,
        ipAddress: ip,
        action: PortalUserHistoryActions.PageVisit,
      });
    }
  }, [location.pathname, ip]);

  const openAccount = Boolean(anchorElAccount);
  useEffect(() => {
    const qs = queryString.parse(location.search);
    if (qs.invitation) {
      loginWithRedirect({
        authorizationParams: {
          organization: qs.organization,
          invitation: qs.invitation,
          redirect_uri: redirectRoute,
        },
      });
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      const getUserMetadata = async () => {
        try {
          const accessToken = await getAccessTokenSilently({
            audience: getAudience(),
          });
          const decoded = jwtDecode(accessToken);
          const id = decoded.sub;
          localStorage.setItem(LocalStorageKeys.NameId, id);
          const userRoles = decoded["https://lynx-esg.com/roles"];
          if (userRoles) {
            localStorage.setItem(LocalStorageKeys.UserRoles, userRoles);
          } else {
            showAlert("error", "No roles associated to user.");
            await performAuth0Logout({
              logoutParams: { returnTo: redirectRoute },
            });
          }
        } catch (e) {
          await performAuth0Logout({
            logoutParams: { returnTo: redirectRoute },
          });
        }
      };

      getUserMetadata();
    }
  }, [isAuthenticated]);

  const handleLogin = async () => {
    await loginWithRedirect({
      authorizationParams: {
        organization: portal.auth0OrganizationId, // Must be the actual org ID
        redirect_uri: redirectRoute,
      },
    });
  };

  const handleLogout = () => {
    logUserHistory({
      url: location.pathname,
      ipAddress: ip,
      action: PortalUserHistoryActions.Logout,
    }).then(async () => {
      await performAuth0Logout({ logoutParams: { returnTo: redirectRoute } });
      setAlertMessage("Signed out successfully.");

      handleCloseAccount();
    });
  };

  const handleCloseAccount = () => {
    setAnchorElAccount(null);
  };

  const getInitials = () => {
    let nameString = "";
    if (account.firstName) nameString += _.first(account.firstName);
    if (account.lastName) nameString += _.first(account.lastName);
    return nameString;
  };
  const handleAccountClick = (event) => {
    if (openAccount) {
      setAnchorElAccount(null);
    } else {
      setAnchorElAccount(event.currentTarget);
    }
  };
  const profileForm = () => {
    return (
      <Grid>
        <Grid.Row>
          <Grid.Col width={12} md={12}>
            <Form.Group label="Email address" isRequired>
              <Form.Input
                type="email"
                placeholder="Email"
                disabled
                value={formState.email}
                name="email"
                onChange={handleInputChange}
              />
            </Form.Group>
          </Grid.Col>
          <Grid.Col width={12} md={12}>
            <Button
              variant="outlined"
              color="error"
              className="mb-5"
              onClick={() => setDeactivate(true)}
            >
              Deactivate account
            </Button>
          </Grid.Col>
          <Grid.Col width={12} md={12}>
            <Form.Group label="First Name" isRequired>
              <Form.Input
                type="text"
                placeholder="First Name"
                value={formState.firstName}
                error={formState.firstNameError}
                name="firstName"
                onChange={handleInputChange}
              />
            </Form.Group>
          </Grid.Col>
          <Grid.Col width={12} md={12}>
            <Form.Group label="Last Name" isRequired>
              <Form.Input
                type="text"
                placeholder="Last Name"
                value={formState.lastName}
                error={formState.lastNameError}
                name="lastName"
                onChange={handleInputChange}
              />
            </Form.Group>
          </Grid.Col>
        </Grid.Row>
      </Grid>
    );
  };
  const validateDataForSave = () => {
    const newState = { ...formState };
    let isFormValid = false;
    validationService.validateRequiredField(
      newState,
      "firstName",
      "firstNameError",
      "First Name"
    );
    validationService.validateRequiredField(
      newState,
      "lastName",
      "lastNameError",
      "Last Name"
    );

    isFormValid = !validationService.hasError(
      newState,
      "firstNameError",
      "lastNameError"
    );

    if (!isFormValid) {
      setFormState(newState);
      setAlertMessage("Form is not valid for saving.");
    }
    return isFormValid;
  };
  const handleSaveUser = () => {
    if (!validateDataForSave()) {
      return;
    }
    const dto = {
      firstName: formState.firstName,
      lastName: formState.lastName,
    };
    updatePortalUserSelf(dto)
      .then((res) => {
        dispatch(loadAccount(true));
        setAlertMessage("Profile updated successfully.");
      })
      .catch((err) => {
        setAlertMessage(err.response.data.message);
      })
      .finally(() => {
        setShowProfile(false);
      });
  };

  const handleDeactivateUser = () => {
    deactivateUser(location.pathname, ip).then(() => {
      //TODO fix for new logot
      //dispatch(logoutUser());
      navigate("/wyloo");
      setAlertMessage("Account deactivated successfully.");
    });
  };
  return (
    <ThemeProvider theme={wylooTheme}>
      <div className="wyloo-navbar p-2">
        <img
          className="mt-1"
          src="https://lynx-public.s3.amazonaws.com/wyloo/portal/images/wyloo-header-logo.svg"
          alt="Wyloo"
        />
        {width > 800 && (
          <img
            className="ml-5"
            style={{ paddingTop: "0.4rem" }}
            src="https://lynx-public.s3.amazonaws.com/wyloo/portal/images/eagles-nest-banner.svg"
            alt="Wyloo"
          />
        )}

        <div className="lynx-logo-container pointer">
          <img
            className="lynx-logo-wyloo"
            height={300}
            src={LynxLogo}
            alt="Wyloo"
          />
        </div>
        {!portal.isLoading && portal.isEnabled && (
          <>
            {isAuthenticated && (
              <Avatar
                className="avatar-wyloo pointer"
                src="/broken-image.jpg"
                onClick={handleAccountClick}
              >
                {getInitials(account)}
              </Avatar>
            )}
            {!isAuthenticated &&
              !portal.isLoading &&
              portal.auth0OrganizationId && (
                <>
                  <Button
                    className="login-button-wyloo mt-2 mr-1"
                    onClick={handleLogin}
                  >
                    Sign in
                  </Button>{" "}
                  <Button
                    onClick={() => {
                      window.open(
                        "https://wyloo.com/wylooeaglesnest/connect/",
                        "_blank"
                      );
                    }}
                    className="contact-button-wyloo mt-2"
                  >
                    Contact
                  </Button>
                </>
              )}
          </>
        )}
      </div>
      <Menu
        className="nav-menu"
        anchorEl={anchorElAccount}
        open={openAccount}
        disableScrollLock={true}
        onClose={handleCloseAccount}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        <MenuItem
          onClick={() => {
            setShowProfile(true);
            handleCloseAccount();
          }}
        >
          <ListItemText>Profile</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleLogout();
          }}
        >
          <ListItemText>Sign out</ListItemText>
        </MenuItem>
      </Menu>
      <LynxDialog
        open={showProfile}
        handleClose={() => setShowProfile(false)}
        dialogContent={profileForm()}
        handleSave={handleSaveUser}
        title="Edit Profile"
      />
      <LynxDialog
        open={deactivate}
        handleClose={() => setDeactivate(false)}
        description={
          "Are you sure you want to deactivate your account? You will need to re-regiester to access the portal."
        }
        handleConfirm={() => {
          handleDeactivateUser();
          setDeactivate(false);
        }}
        title="Edit Profile"
      />
      {alertMessage && (
        <LynxDialog
          open={alertMessage}
          handleConfirm={() => {
            setAlertMessage(null);
          }}
          title={alertMessage}
        />
      )}
      {showPendingRegistration && (
        <LynxDialog
          open={showPendingRegistration}
          handleConfirm={() => {
            setShowPendingRegistration(false);
            performAuth0Logout({
              logoutParams: { returnTo: redirectRoute },
            });
          }}
          title={"Account pending approval"}
          description={
            "Your account is pending approval. You will receive an email once your account has been approved."
          }
        />
      )}
      {showDisabled && (
        <LynxDialog
          open={showDisabled}
          handleConfirm={() => {
            setShowDisabled(false);
            performAuth0Logout({
              logoutParams: { returnTo: redirectRoute },
            });
          }}
          title={"Account has been disabled."}
        />
      )}
    </ThemeProvider>
  );
}
